import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Clock,
  User,
  Edit,
  Trash2,
  Plus,
  Copy,
  CheckCircle,
  XCircle,
  Filter,
  Download,
} from "lucide-react";
import { getPricingRuleAuditLogThunk } from "../../../store/thunks/pricingThunks";
import {
  selectRuleAuditLog,
  selectAuditLogPagination,
  selectAuditLogLoading,
  selectAuditLogError,
} from "../../../store/slices/pricingSlice";
import LoadingSpinner from "../../../components/common/Spinner";
import Pagination from "../../../components/common/Pagination";

const AuditLog = ({ ruleId, ruleName }) => {
  const dispatch = useDispatch();
  const auditLogs = useSelector(selectRuleAuditLog);
  const pagination = useSelector(selectAuditLogPagination);
  const loading = useSelector(selectAuditLogLoading);
  const error = useSelector(selectAuditLogError);

  const [filter, setFilter] = useState("all");
  const [dateRange, setDateRange] = useState("30d");

  useEffect(() => {
    dispatch(
      getPricingRuleAuditLogThunk({
        ruleId,
        page: pagination.page,
        limit: pagination.limit,
        action: filter,
        dateRange,
      })
    );
  }, [dispatch, ruleId, pagination.page, filter, dateRange]);

  const getActionIcon = (action) => {
    switch (action) {
      case "CREATED":
        return <Plus className="w-4 h-4 text-success" />;
      case "UPDATED":
        return <Edit className="w-4 h-4 text-warning" />;
      case "DELETED":
        return <Trash2 className="w-4 h-4 text-error" />;
      case "ACTIVATED":
        return <CheckCircle className="w-4 h-4 text-success" />;
      case "DEACTIVATED":
        return <XCircle className="w-4 h-4 text-error" />;
      case "CLONED":
        return <Copy className="w-4 h-4 text-primary" />;
      default:
        return <Clock className="w-4 h-4 text-neutral" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "CREATED":
        return "bg-success-light text-success border-success";
      case "UPDATED":
        return "bg-warning-light text-warning border-warning";
      case "DELETED":
        return "bg-error-light text-error border-error";
      case "ACTIVATED":
        return "bg-success-light text-success border-success";
      case "DEACTIVATED":
        return "bg-error-light text-error border-error";
      case "CLONED":
        return "bg-primary-light text-primary border-primary";
      default:
        return "bg-neutral-lightest text-neutral border-neutral-light";
    }
  };

  const handleExportLog = () => {
    const csvContent = [
      [
        "Timestamp",
        "Action",
        "Field",
        "Old Value",
        "New Value",
        "User",
        "IP",
        "Reason",
      ],
      ...auditLogs.map((log) => [
        new Date(log.timestamp).toISOString(),
        log.action,
        log.field || "-",
        log.oldValue || "-",
        log.newValue || "-",
        log.user || "-",
        log.ip || "-",
        log.reason || "-",
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-log-${ruleId}-${Date.now()}.csv`;
    link.click();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="bg-error-light border border-error rounded-lg p-6 text-center">
        <p className="text-error font-medium">Failed to load audit log</p>
        <p className="text-neutral-dark mt-2">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-dark">
            Audit Log: {ruleName}
          </h2>
          <p className="text-neutral mt-1">
            Complete history of all changes made to this rule
          </p>
        </div>
        <button
          onClick={handleExportLog}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-neutral-light text-neutral-dark rounded-lg hover:bg-neutral-lightest transition-colors"
        >
          <Download className="w-5 h-5" />
          <span>Export Log</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 border border-neutral-light">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-neutral" />
            <span className="text-sm font-medium text-neutral-dark">
              Filter by:
            </span>
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-neutral-light rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Actions</option>
            <option value="CREATED">Created</option>
            <option value="UPDATED">Updated</option>
            <option value="DELETED">Deleted</option>
            <option value="ACTIVATED">Activated</option>
            <option value="DEACTIVATED">Deactivated</option>
            <option value="CLONED">Cloned</option>
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-neutral-light rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-lg border border-neutral-light">
        {auditLogs.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="w-12 h-12 text-neutral mx-auto mb-3" />
            <p className="text-neutral">No audit logs found</p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-light">
            {auditLogs.map((log, index) => (
              <div
                key={index}
                className="p-6 hover:bg-neutral-lightest transition-colors"
              >
                <div className="flex items-start space-x-4">
                  {/* Timeline Dot */}
                  <div className="flex flex-col items-center">
                    <div
                      className={`p-2 rounded-full border-2 ${getActionColor(
                        log.action
                      )}`}
                    >
                      {getActionIcon(log.action)}
                    </div>
                    {index < auditLogs.length - 1 && (
                      <div className="w-0.5 h-full bg-neutral-light mt-2" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-neutral-dark">
                          {log.action.charAt(0) +
                            log.action.slice(1).toLowerCase()}{" "}
                          {log.field}
                        </h4>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-neutral">
                          <div className="flex items-center space-x-1">
                            <User className="w-4 h-4" />
                            <span>
                              {log.user} ({log.userId || "N/A"})
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${getActionColor(
                          log.action
                        )}`}
                      >
                        {log.action}
                      </span>
                    </div>

                    {/* Changes */}
                    {log.oldValue && log.newValue && (
                      <div className="bg-neutral-lightest rounded-lg p-4 mt-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-neutral mb-1">
                              Previous Value
                            </p>
                            <p className="text-sm font-medium text-neutral-dark line-through">
                              {JSON.stringify(log.oldValue)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral mb-1">
                              New Value
                            </p>
                            <p className="text-sm font-medium text-success">
                              {JSON.stringify(log.newValue)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Reason */}
                    {log.reason && (
                      <div className="mt-3 p-3 bg-primary-light rounded-lg border border-primary">
                        <p className="text-xs font-medium text-primary mb-1">
                          Reason for Change
                        </p>
                        <p className="text-sm text-neutral-dark">
                          {log.reason}
                        </p>
                      </div>
                    )}

                    {/* Additional Info */}
                    {log.ip && (
                      <div className="mt-3 text-xs text-neutral">
                        IP: {log.ip}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={(page) =>
            dispatch(
              getPricingRuleAuditLogThunk({
                ruleId,
                page,
                limit: pagination.limit,
                action: filter,
                dateRange,
              })
            )
          }
        />
      )}
    </div>
  );
};

export default AuditLog;
